import axios from 'axios'
import { hasIn } from 'lodash'
import uuid from 'uuid/v4'
import { getAnonymousId, identifyUser } from '@components/integrations/Segment'
import { createHash } from 'crypto'

const TIMEOUT_LENGTH = 5

// TODO: Change to furniture.com API? Right now browser will show auth-token.
// Token exposure isn't an issue if we're whitelisting URLs for address lookup
// eslint-disable-next-line
export let fetchAddressLookup = async address => {
  const transactionId = uuid()
  if (!address) {
    return null
  }
  try {
    let edqResponse = null
    edqResponse = await axios.post(
      'https://api.experianaperture.io/address/search/v1',
      {
        country_iso: 'USA',
        datasets: ['us-address'],
        max_suggestions: 10,
        components: {
          unspecified: [`${address}`],
        },
      },
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Auth-Token': `${process.env.GATSBY_QAS_AUTH_TOKEN}`,
          'Timeout-Seconds': TIMEOUT_LENGTH,
        },
      },
    )
    return edqResponse.data
  } catch (error) {
    if (error.message.includes('timeout')) {
      console.error('QAS Network Timeout', transactionId)
    } else {
      console.error('QAS Network Failure', transactionId)
    }
    return null
  }
}

export const fetchAddressFormat = async id => {
  const transactionId = uuid()
  if (!id) {
    return null
  }
  try {
    let edqResponse = null
    edqResponse = await axios.get(`https://api.experianaperture.io/address/format/v1/${id}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Auth-Token': `${process.env.GATSBY_QAS_AUTH_TOKEN}`,
        'Timeout-Seconds': TIMEOUT_LENGTH,
      },
    })
    const address = edqResponse?.data?.result?.address || {}
    return {
      address1: address?.address_line_1 ?? '',
      address2: address?.address_line_2 ?? '',
      city: address?.locality ?? '',
      state: address?.region ?? '',
      zip: address?.postal_code?.split('-')[0] ?? '',
      plus4: address?.postal_code?.split('-')[1] ?? '',
    }
  } catch (error) {
    if (error.message.includes('timeout')) {
      console.error('QAS Network Timeout', transactionId)
    } else {
      console.error('QAS Network Failure', transactionId)
    }
    throw error
  }
}

export const setSecretHeader = (body, authtoken) => {
  const secret = 'movethemicrowave'
  const headers = {}
  if (body.orderId) {
    const stringToHash = `${body.orderId}-${secret}`
    const hash = createHash('sha256')
      .update(stringToHash)
      .digest('hex')
    headers.sddhe = hash
  }
  if (authtoken) {
    headers.authtoken = authtoken
  }
  return { headers }
}

export const fetchPromotions = body =>
  axios.post(`${process.env.GATSBY_PROMOTIONS_SERVICE_URL}/get_pricing/`, body).then(response => response.data)

/* Order Service */
export const getOrder = body =>
  axios
    .post(`${process.env.GATSBY_ORDER_SERVICE_URL}/getOrder/`, body, setSecretHeader(body))
    .then(response => response.data)

export const getToken = body =>
  axios
    .post(`${process.env.GATSBY_ORDER_SERVICE_URL}/v2/token/`, body, setSecretHeader(body))
    .then(response => response.data)

export const getPayOnlineToken = storeOrder =>
  axios.post(`${process.env.GATSBY_ORDER_MANAGEMENT_URL}/flexV2Token/`, { storeOrder }).then(response => response.data)

export const createOrder = body =>
  axios
    .post(`${process.env.GATSBY_ORDER_SERVICE_URL}/createOrder/`, body, setSecretHeader(body))
    .then(response => response.data)

export const updateLineItems = body =>
  axios
    .post(`${process.env.GATSBY_ORDER_SERVICE_URL}/updateLineItems/`, body, setSecretHeader(body))
    .then(response => response.data)

export const updateAddress = body =>
  axios
    .post(`${process.env.GATSBY_ORDER_SERVICE_URL}/updateAddress/`, body, setSecretHeader(body))
    .then(response => response.data)

export const updateDelivery = body =>
  axios
    .post(`${process.env.GATSBY_ORDER_SERVICE_URL}/updateDelivery/`, body, setSecretHeader(body))
    .then(response => response.data)

export const updatePayment = body =>
  axios
    .post(`${process.env.GATSBY_ORDER_SERVICE_URL}/updatePayment/`, body, setSecretHeader(body))
    .then(response => response.data)

export const createBitPayInvoice = body =>
  axios.post(`${process.env.GATSBY_BITPAY_SERVICE_URL}/invoice/`, body).then(response => response.data)

export const createPayPalTransaction = body =>
  axios.post(`${process.env.GATSBY_PAYPAL_SERVICE_URL}/v2/orders`, body).then(response => response.data)

export const getPayPalTransaction = id =>
  axios.get(`${process.env.GATSBY_PAYPAL_SERVICE_URL}/v2/orders/${id}`).then(response => response.data)

export const placeOrder = (orderId, session, TL_RTG, token) => {
  const body = { orderId, session, TL_RTG }
  return axios
    .post(`${process.env.GATSBY_ORDER_SERVICE_URL}/placeOrder/`, body, setSecretHeader(body, token))
    .then(response => response.data)
}

export const getStoreCart = async ({ email, storeCartId }) => {
  const options = { headers: { 'x-api-key': process.env.GATSBY_STORECART_API_KEY } }
  const url = `${process.env.GATSBY_STORECART_SERVICE_URL}/storeCartByEmail?storeCartId=${storeCartId}&email=${email}`
  const response = await axios.get(url, options)
  return response.data
}

export const getM1ConfiguratorCartItem = async cartId =>
  axios.get(`${process.env.GATSBY_CONFIGURATOR_ONLINE_CART_URL}?cartId=${cartId}`).then(response => response.data)

export const checkSplitElig = (orderNumber, zipCode) =>
  axios
    .get(`${process.env.GATSBY_ORDER_SERVICE_URL}/splitCheck/${orderNumber}/${zipCode}`)
    .then(response => response.data)

export const getSimilarItems = body =>
  axios.post(`${process.env.GATSBY_RECOMMENDATION_SERVICE}/similarItems`, body).then(response => response.data)

export const getWellsFargoFinancePlans = async region =>
  axios
    .get(`${process.env.GATSBY_ORDER_MANAGEMENT_URL}/wfg/plans`)
    .then(response => response?.data[region]?.slice(0, 2))

export const getWellsFargoOTB = async body =>
  axios
    .post(`${process.env.GATSBY_ORDER_SERVICE_URL}/wfg/balance`, body, setSecretHeader(body))
    .then(response => response.data)

export const getWellsFargoPromoTerms = async body =>
  axios
    .post(`${process.env.GATSBY_ORDER_SERVICE_URL}/wfg/promodisclosure`, body, setSecretHeader(body))
    .then(response => response.data)
